import { Badge, Button, FormField, Input } from "@design";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CredentialMetadata, ExtensionResponse } from "@/api";
import { useAppForm } from "@/components/form/form";
import { PageSection, SettingsList } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import type { IntegrationActions } from "./useIntegrationActions";

/** A credential field: its schema (label, type) joined with its stored status. */
export interface ConfigField {
  key: string;
  label: string;
  description?: string;
  required: boolean;
  secret: boolean;
  status?: CredentialMetadata;
  /** Stored but not described by the integration's fields (left by older features); shown read-only. */
  extra?: boolean;
}

interface SchemaField {
  key?: string;
  label?: string;
  description?: string;
  type?: string;
  required?: boolean;
}

/** Schema fields first (in order), then any stored credentials the schema doesn't describe. */
export function configFields(extension: ExtensionResponse): ConfigField[] {
  const schema = ((extension.credentialsConfig?.fields ?? []) as SchemaField[]).filter((f) => f.key);
  const stored = new Map((extension.credentials ?? []).map((c) => [c.key, c]));
  const fields: ConfigField[] = schema.map((f) => ({
    key: f.key as string,
    label: f.label || (f.key as string),
    description: f.description,
    required: Boolean(f.required),
    secret: (f.type ?? "secret") === "secret",
    status: stored.get(f.key as string),
  }));
  for (const c of extension.credentials ?? []) {
    if (!fields.some((f) => f.key === c.key)) {
      fields.push({ key: c.key, label: c.key, required: false, secret: c.isSecret ?? true, status: c, extra: true });
    }
  }
  return fields;
}

function StatusBadge({ field }: Readonly<{ field: ConfigField }>) {
  const { t } = useTranslation("adminIntegrations");
  if (field.status?.hasDbValue) return <Badge variant="success">{t("detail.credential.saved")}</Badge>;
  if (field.status?.isConfigured) {
    return <Badge variant="success">{t("detail.credential.env", { name: field.status.envVarName })}</Badge>;
  }
  return (
    <Badge variant={field.required ? "warning" : "neutral"}>
      {t(field.required ? "detail.credential.missing" : "edit.optional")}
    </Badge>
  );
}

function CredentialForm({
  field,
  actions,
  onDone,
}: Readonly<{ field: ConfigField; actions: IntegrationActions; onDone: () => void }>) {
  const { t } = useTranslation("adminIntegrations");
  const id = useId();
  const form = useAppForm({
    defaultValues: { value: field.secret ? "" : (field.status?.currentValue ?? "") },
    onSubmit: async ({ value }) => {
      if (await actions.setCredential(field.key, field.label, value.value.trim())) onDone();
    },
  });
  return (
    <form
      aria-label={t("edit.formLabel", { field: field.label })}
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.Field
        name="value"
        validators={{ onChange: ({ value }) => (value.trim() ? undefined : t("edit.required")) }}
      >
        {(f) => (
          <FormField
            label={field.label}
            htmlFor={id}
            error={f.state.meta.isTouched ? f.state.meta.errors[0] : undefined}
          >
            <Input
              id={id}
              type={field.secret ? "password" : "text"}
              autoComplete="off"
              value={f.state.value}
              onChange={(e) => f.handleChange(e.target.value)}
              onBlur={f.handleBlur}
            />
          </FormField>
        )}
      </form.Field>
      {field.secret && (
        <Text variant="caption" tone="tertiary">
          {t("edit.secretHint")}
        </Text>
      )}
      <form.AppForm>
        <form.SubmitActions onCancel={onDone} submitLabel={t("edit.save")} />
      </form.AppForm>
    </form>
  );
}

function ConfigRow({ field, actions }: Readonly<{ field: ConfigField; actions: IntegrationActions }>) {
  const { t } = useTranslation("adminIntegrations");
  const [editing, setEditing] = useState(false);
  const shown = !field.secret && field.status?.currentValue;
  return (
    <div className="flex flex-col gap-3 py-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-2">
            <Text as="span" variant="body">
              {field.label}
            </Text>
            <StatusBadge field={field} />
          </span>
          {field.description && (
            <Text variant="body-sm" tone="tertiary">
              {field.description}
            </Text>
          )}
          {shown && (
            <Text variant="body-sm" tone="secondary" className="break-all">
              {field.status?.currentValue}
            </Text>
          )}
        </div>
        {!editing && (
          <div className="flex shrink-0 gap-2">
            <Button
              variant="secondary"
              size="sm"
              aria-label={t(field.status?.isConfigured ? "edit.replaceField" : "edit.setField", { field: field.label })}
              onClick={() => setEditing(true)}
            >
              {t(field.status?.isConfigured ? "edit.replace" : "edit.set")}
            </Button>
            {field.status?.hasDbValue && (
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("edit.clearField", { field: field.label })}
                loading={actions.clearing === field.key}
                onClick={() => actions.clearCredential(field.key, field.label)}
              >
                {t("edit.clear")}
              </Button>
            )}
          </div>
        )}
      </div>
      {field.status?.hasDbValue && field.status.isEnvOverride && field.status.envVarName && (
        <Text variant="caption" tone="tertiary">
          {t("edit.overridesEnv", { name: field.status.envVarName })}
        </Text>
      )}
      {editing && <CredentialForm field={field} actions={actions} onDone={() => setEditing(false)} />}
    </div>
  );
}

/** Values the integration doesn't define: listed for awareness only, collapsed and not editable. */
function OtherValues({ fields }: Readonly<{ fields: ConfigField[] }>) {
  const { t } = useTranslation("adminIntegrations");
  if (fields.length === 0) return null;
  return (
    <details className="group flex flex-col gap-2">
      <summary className="btn-focus-ring cursor-pointer rounded">
        <Text as="span" variant="body-sm" tone="secondary">
          {t("edit.otherValues", { count: fields.length })}
        </Text>
      </summary>
      <div className="flex flex-col gap-2 pt-2">
        <Text variant="caption" tone="tertiary">
          {t("edit.otherValuesBody")}
        </Text>
        <ul aria-label={t("edit.otherValuesLabel")} className="flex flex-col gap-1">
          {fields.map((field) => (
            <li key={field.key} className="flex items-center gap-2">
              <Text as="span" variant="body-sm" className="[font:var(--type-code)]">
                {field.key}
              </Text>
              <StatusBadge field={field} />
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}

/** Credentials and settings, editable (desktop). Values are stored encrypted; secrets are never shown. */
export function ConfigurationSection({
  extension,
  actions,
}: Readonly<{ extension: ExtensionResponse; actions: IntegrationActions }>) {
  const { t } = useTranslation("adminIntegrations");
  const all = configFields(extension);
  const fields = all.filter((f) => !f.extra);
  return (
    <PageSection title={t("edit.configuration")} description={t("edit.configurationBody")}>
      {fields.length === 0 ? (
        <Text variant="body-sm" tone="tertiary">
          {t("detail.noCredentials")}
        </Text>
      ) : (
        <SettingsList>
          {fields.map((field) => (
            <ConfigRow key={field.key} field={field} actions={actions} />
          ))}
        </SettingsList>
      )}
      <OtherValues fields={all.filter((f) => f.extra)} />
    </PageSection>
  );
}
